/**
 * @file SubmissionProgressModal.tsx
 * @description
 * A React component that displays a full-screen, semi-transparent overlay showing
 * step-by-step progress for a multi-step submission process. It can also indicate
 * success or error states and provide actions such as retry or close.
 *
 * Key features:
 * 1. Full-screen overlay with Tailwind styling for a modal-like experience.
 * 2. Progress bar or spinner to visualize ongoing steps.
 * 3. Step-based text feedback (e.g., "Checking required form fields...", "Storing locked form as PDF...").
 * 4. Error and success states with the ability to show an error message or final success message.
 * 5. Close or retry callbacks for user actions.
 *
 * @dependencies
 * - React: for building the component.
 * - Tailwind CSS: for utility-first styling.
 * - TypeScript: for strict type definitions.
 *
 * @notes
 * - Typically invoked from `SubmissionForm.tsx` after final user submission, or from any other
 *   multi-step workflow that might require a blocking overlay.
 * - The parent can manage which step is active, the total step count, and pass error messages if needed.
 * - Step transitions can be triggered via props, so each time the parent completes a step,
 *   it increments `currentStep` or updates `statusMessage`.
 *
 * Example usage:
 * ```jsx
 * const [isModalOpen, setIsModalOpen] = useState(false);
 * const [currentStep, setCurrentStep] = useState(0);
 * const steps = [
 *   "Checking required form fields...",
 *   "Storing locked form as PDF...",
 *   "Versioning uploaded documents...",
 *   "Redirecting to dashboard..."
 * ];
 *
 * return (
 *   <SubmissionProgressModal
 *     isOpen={isModalOpen}
 *     stepMessages={steps}
 *     currentStep={currentStep}
 *     onClose={() => setIsModalOpen(false)}
 *     hasError={errorOccurred}
 *     errorMessage={errorMsg}
 *     hasSuccess={finishedSuccessfully}
 *     successMessage="Submission complete! Redirecting..."
 *     onRetry={attemptSubmitAgain}  // optional
 *   />
 * );
 * ```
 */

import React from 'react';
import { Transition } from '@headlessui/react';

/**
 * SubmissionProgressModalProps
 * ----------------------------
 * Defines the props for SubmissionProgressModal.
 *
 * @property isOpen          - Controls whether the modal overlay is rendered.
 * @property stepMessages    - Array of string messages for each step.
 * @property currentStep     - Zero-based index of the currently active step in stepMessages.
 * @property hasError        - Whether an error occurred; if true, shows error UI.
 * @property errorMessage    - String describing the error; displayed if hasError is true.
 * @property hasSuccess      - Whether the process completed successfully; if true, shows success UI.
 * @property successMessage  - String describing success. Only displayed if hasSuccess is true.
 * @property onClose         - Callback invoked when user wants to close the modal (e.g., an "X" or "Close" button).
 * @property onRetry         - Optional callback for "Retry" in case of error.
 * @property showCloseButton - If true, show a "Close" button even in normal states (some flows might hide it).
 */
interface SubmissionProgressModalProps {
  isOpen: boolean;
  stepMessages: string[];
  currentStep: number;
  hasError?: boolean;
  errorMessage?: string;
  hasSuccess?: boolean;
  successMessage?: string;
  onClose?: () => void;
  onRetry?: () => void;
  showCloseButton?: boolean;
}

/**
 * SubmissionProgressModal
 * -----------------------
 * A modal overlay that displays the current step out of stepMessages, an optional spinner or
 * progress bar, and handles error/success states.
 *
 * @component
 * @param props {SubmissionProgressModalProps}
 * @returns JSX Element if isOpen is true, otherwise null.
 */
export const SubmissionProgressModal: React.FC<SubmissionProgressModalProps> = (props) => {
  const {
    isOpen,
    stepMessages,
    currentStep,
    hasError,
    errorMessage,
    hasSuccess,
    successMessage,
    onClose,
    onRetry,
    showCloseButton,
  } = props;

  // Number of steps is derived from stepMessages, but we require at least 1 step to avoid edge cases
  const totalSteps = stepMessages.length || 1;
  // Bound the currentStep index if out of range
  const boundedStep = Math.min(Math.max(currentStep, 0), totalSteps - 1);

  // Calculate a percent-based progress (0 to 100). If there's only 1 step, treat it as partial or indefinite.
  const progressPercent = Math.round(((boundedStep + 1) / totalSteps) * 100);

  // If we have an error or success, the "progress" portion is effectively done, so we might skip the spinner.
  const showProgress = !hasError && !hasSuccess;

  // The text to display for the current step
  const currentStepMessage = stepMessages[boundedStep] || "Processing...";

  return (
    <Transition show={isOpen}>
      <Transition.Child
        enter="transition-opacity duration-200 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          aria-hidden="true"
        />
      </Transition.Child>

      <Transition.Child
        enter="transition duration-200 ease-out transform"
        enterFrom="opacity-0 -translate-y-6"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-300 ease-in transform"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-6"
      >
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-white rounded-md shadow-lg p-6 max-w-md w-full mx-4">
            {/* Header / Title */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {hasError
                  ? "An Error Occurred"
                  : hasSuccess
                  ? "Completed Successfully"
                  : "Please Wait..."}
              </h2>

              {/* Close 'X' only if showCloseButton is true or we have success/error */}
              {(showCloseButton || hasError || hasSuccess) && onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Body / Status */}
            {hasError ? (
              <div className="text-sm text-red-600 mb-4">
                {errorMessage || "Something went wrong. Please try again."}
              </div>
            ) : hasSuccess ? (
              <div className="text-sm text-green-700 mb-4">
                {successMessage || "Your process has completed successfully!"}
              </div>
            ) : (
              <div className="text-sm text-gray-700 mb-4">
                {currentStepMessage}
              </div>
            )}

            {/* Progress bar if no error/success */}
            {showProgress && (
              <div className="mb-4">
                <div className="w-full h-2 bg-gray-200 rounded">
                  <div
                    className="h-2 bg-blue-400 transition-all rounded"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 text-right">
                  {progressPercent}%
                </p>
              </div>
            )}

            {/* Spinner if no error/success, or minimalistic indefinite loader */}
            {showProgress && (
              <div className="flex justify-center items-center mb-4">
                <svg
                  className="animate-spin h-8 w-8 text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              </div>
            )}

            {/* Actions */}
            {(hasError || hasSuccess || showCloseButton) && (
              <div className="flex justify-end space-x-2">
                {hasError && onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="px-4 py-2 bg-[#F55D3E] text-white rounded-md hover:bg-[#F9A594] transition"
                  >
                    Retry
                  </button>
                )}
                {(hasError || hasSuccess || showCloseButton) && onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );
};
